<template>
  <UPage class="flex-1 flex flex-col min-h-0" :ui="{ center: 'flex flex-col flex-1 min-h-0' }">
    <UPageHeader
      :title="(addressDetails.fldSuchBeg as string) || 'Adressdetails'"
      :description="`Nummer: ${addressId}`"
    >
      <template #links>
        <UButton
          icon="i-heroicons-arrow-left"
          label="Zurück zu Adressen"
          variant="ghost"
          @click="$router.push('/microtech-hub/addresses')"
        />
      </template>
    </UPageHeader>

    <UPageBody class="flex flex-col min-h-0 h-full">
      <!-- Column Configuration Modal -->
      <ColumnSelectionModal
        ref="columnModal"
        :all-columns="allPossibleColumns"
        :initial-selected="selectedTabColumnsMapped"
        :loading="fetchingSchema"
        :title="modalTitle"
        :description="modalDescription"
        @save="onSaveColumns"
        @reset="resetToDefault"
      />

      <UTabs
        v-model="activeTab"
        :items="tabs"
        class="flex-1 flex flex-col min-h-0 h-full"
        :ui="{ content: 'flex-1 flex flex-col min-h-0 h-full' }"
      >
        <template #details>
          <UCard>
            <template #header>
              <div class="flex items-center justify-between">
                <h3 class="text-base font-semibold">Stammdaten</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('details')"
                />
              </div>
            </template>
            <UAlert
              v-if="schemaErrorByTab.details"
              class="mx-4 mt-4"
              color="warning"
              variant="subtle"
              icon="i-lucide-alert-triangle"
              title="Die Feldinformationen konnten nicht von microtech geladen werden."
              description="Es werden die Standardfelder angezeigt. Über „Felder anpassen“ wird das Laden erneut versucht."
            />
            <UAlert
              v-if="!loading && detailsError"
              class="mx-4 mt-4"
              color="error"
              variant="subtle"
              icon="i-lucide-alert-triangle"
              title="Die Adressdaten konnten nicht geladen werden."
              description="microtech hat keine Daten zu dieser Adresse geliefert. Bitte versuchen Sie es später erneut."
            />
            <div v-if="loading" class="flex justify-center py-10">
              <UIcon class="w-10 h-10 animate-spin text-muted" name="i-heroicons-arrow-path" />
            </div>
            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
              <div
                v-for="field in detailFields"
                :key="field.accessorKey"
                class="border-b border-default pb-2"
              >
                <p class="text-xs font-medium text-muted uppercase tracking-wider">
                  {{ field.label }}
                </p>
                <p class="mt-1 text-sm text-highlighted">
                  {{ resolveValue(addressDetails, field) || '-' }}
                </p>
              </div>
            </div>
          </UCard>
        </template>

        <template #ansprechpartner>
          <PortalTable
            ref="ansprechpartnerTable"
            :columns-loading="fetchingSchema"
            :data="relatedData.ansprechpartner"
            :loading="loadingRelated.ansprechpartner"
            :selected-columns="selectedTabColumns('ansprechpartner')"
            :all-columns="availableColumns('ansprechpartner')"
            :total="relatedData.ansprechpartner.length"
            sort-storage-key="address_detail_ansprechpartner_sort"
            class="w-full"
            @update:columns="onSaveColumns"
            @reset:columns="resetToDefault"
          >
            <template #header>
              <div class="flex items-center justify-between p-4 border-b border-default">
                <h3 class="text-base font-semibold">Ansprechpartner</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('ansprechpartner')"
                />
              </div>
            </template>
          </PortalTable>
        </template>

        <template #anschriften>
          <PortalTable
            ref="anschriftenTable"
            :columns-loading="fetchingSchema"
            :data="relatedData.anschriften"
            :loading="loadingRelated.anschriften"
            :selected-columns="selectedTabColumns('anschriften')"
            :all-columns="availableColumns('anschriften')"
            :total="relatedData.anschriften.length"
            sort-storage-key="address_detail_anschriften_sort"
            class="w-full"
            @update:columns="onSaveColumns"
            @reset:columns="resetToDefault"
          >
            <template #header>
              <div class="flex items-center justify-between p-4 border-b border-default">
                <h3 class="text-base font-semibold">Anschriften</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('anschriften')"
                />
              </div>
            </template>
          </PortalTable>
        </template>

        <template #projects>
          <PortalTable
            ref="projectsTable"
            :columns-loading="fetchingSchema"
            :data="relatedData.projects"
            :loading="loadingRelated.projects"
            :selected-columns="selectedTabColumns('projects')"
            :all-columns="availableColumns('projects')"
            :total="relatedData.projects.length"
            sort-storage-key="address_detail_projects_sort"
            class="flex-1"
            @select="(row) => onProjectClick(row)"
            @update:columns="onSaveColumns"
            @reset:columns="resetToDefault"
          >
            <template #header>
              <div class="flex items-center justify-between p-4 border-b border-default">
                <h3 class="text-base font-semibold">Projekte</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('projects')"
                />
              </div>
            </template>
            <template #body-bottom>
              <div
                v-if="hasNextPageByTab.projects"
                v-intersection-observer="() => fetchRelatedConRead('projects', true)"
                class="h-1 w-full"
              />
            </template>
          </PortalTable>
        </template>

        <template #projectsarchive>
          <PortalTable
            ref="projectsArchiveTable"
            :columns-loading="fetchingSchema"
            :data="relatedData.projectsarchive"
            :loading="loadingRelated.projectsarchive"
            :selected-columns="selectedTabColumns('projectsarchive')"
            :all-columns="availableColumns('projectsarchive')"
            :total="relatedData.projectsarchive.length"
            sort-storage-key="address_detail_projectsarchive_sort"
            class="flex-1"
            @select="(row) => onProjectArchiveClick(row)"
            @update:columns="onSaveColumns"
            @reset:columns="resetToDefault"
          >
            <template #header>
              <div class="flex items-center justify-between p-4 border-b border-default">
                <h3 class="text-base font-semibold">Projekte Archiv</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('projectsarchive')"
                />
              </div>
            </template>
            <template #body-bottom>
              <div
                v-if="hasNextPageByTab.projectsarchive"
                v-intersection-observer="() => fetchRelatedConRead('projectsarchive', true)"
                class="h-1 w-full"
              />
            </template>
          </PortalTable>
        </template>

        <template #transactions>
          <PortalTable
            ref="transactionsTable"
            :columns-loading="fetchingSchema"
            :data="relatedData.transactions"
            :loading="loadingRelated.transactions"
            :selected-columns="selectedTabColumns('transactions')"
            :all-columns="availableColumns('transactions')"
            :total="relatedData.transactions.length"
            sort-storage-key="address_detail_transactions_sort"
            class="flex-1"
            @select="(row) => onTransactionClick(row)"
            @update:columns="onSaveColumns"
            @reset:columns="resetToDefault"
          >
            <template #header>
              <div class="flex items-center justify-between p-4 border-b border-default">
                <h3 class="text-base font-semibold">Vorgänge</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('transactions')"
                />
              </div>
            </template>
            <template #body-bottom>
              <div
                v-if="hasNextPageByTab.transactions"
                v-intersection-observer="() => fetchRelatedConRead('transactions', true)"
                class="h-1 w-full"
              />
            </template>
          </PortalTable>
        </template>

        <template #transactionsarchive>
          <PortalTable
            ref="transactionsArchiveTable"
            :columns-loading="fetchingSchema"
            :data="relatedData.transactionsarchive"
            :loading="loadingRelated.transactionsarchive"
            :selected-columns="selectedTabColumns('transactionsarchive')"
            :all-columns="availableColumns('transactionsarchive')"
            :total="relatedData.transactionsarchive.length"
            sort-storage-key="address_detail_transactionsarchive_sort"
            class="flex-1"
            @select="(row) => onTransactionArchiveClick(row)"
            @update:columns="onSaveColumns"
            @reset:columns="resetToDefault"
          >
            <template #header>
              <div class="flex items-center justify-between p-4 border-b border-default">
                <h3 class="text-base font-semibold">Vorgänge Archiv</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('transactionsarchive')"
                />
              </div>
            </template>
            <template #body-bottom>
              <div
                v-if="hasNextPageByTab.transactionsarchive"
                v-intersection-observer="() => fetchRelatedConRead('transactionsarchive', true)"
                class="h-1 w-full"
              />
            </template>
          </PortalTable>
        </template>

        <template #dokumente>
          <PortalTable
            ref="dokumenteTable"
            :columns-loading="fetchingSchema"
            :data="relatedData.dokumente"
            :loading="loadingRelated.dokumente"
            :selected-columns="selectedTabColumns('dokumente')"
            :all-columns="availableColumns('dokumente')"
            :total="relatedData.dokumente.length"
            sort-storage-key="address_detail_dokumente_sort"
            class="flex-1"
            highlight-row-key="fldGUID"
            :highlight-row-value="$route.query.highlightGUID as string"
            @select="(row) => onDocumentClick(row)"
            @update:columns="onSaveColumns"
            @reset:columns="resetToDefault"
          >
            <template #header>
              <div class="flex items-center justify-between p-4 border-b border-default">
                <h3 class="text-base font-semibold">Dokumente</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('dokumente')"
                />
              </div>
            </template>
            <template #body-bottom>
              <div
                v-if="hasNextPageByTab.dokumente"
                v-intersection-observer="() => fetchRelatedConRead('dokumente', true)"
                class="h-1 w-full"
              />
            </template>
          </PortalTable>
        </template>
        <template #vatvalidation>
          <VatAddressValidationPage
            :address-nr="addressId"
            :embedded="true"
            :customer="customerNo"
            :tenant="tenantName"
          />
        </template>
        <template #bi>
          <div class="">
            <SupplierAnalysis
              v-if="addressDetails.fnIsVendor"
              :customer-no="customerNo"
              :tenant-name="tenantName"
              :adr-nr="addressId"
              embedded
            />
            <CustomerDashboard
              v-else
              :customer-no="customerNo"
              :tenant-name="tenantName"
              :adr-nr="addressId"
              embedded
            />
          </div>
        </template>
      </UTabs>
    </UPageBody>
  </UPage>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import axios from 'axios'
import PortalTable from '@/components/PortalTable.vue'
import ColumnSelectionModal from '@/components/ColumnSelectionModal.vue'
import VatAddressValidationPage from '@/pages/VatAddressValidationPage.vue'
const CustomerDashboard = defineAsyncComponent(() => import('@/pages/bi/CustomerDashboard.vue'))
const SupplierAnalysis = defineAsyncComponent(() => import('@/pages/bi/SupplierAnalysis.vue'))
import type { FieldInfo } from '@/utils/graphql-introspection'
import {
  fetchRelationFields,
  fetchTableFields,
  isConnectionType,
} from '@/utils/graphql-introspection'
import { useDocumentDownload } from '@/composables/useDocumentDownload'

const props = defineProps<{
  customerNo?: string
  tenantName?: string
  fldAdrNr?: string
}>()

const authStore = useAuthStore()

const customerNo = computed(
  () => props.customerNo || authStore.userInfo?.activeTenant?.customerNo || '',
)
const tenantName = computed(
  () => props.tenantName || authStore.userInfo?.activeTenant?.tenantName || '',
)

const route = useRoute()
const addressId = computed(() => route.params.fldAdrNr as string)

watch(addressId, () => {
  if (customerNo.value && tenantName.value) {
    initData()
  }
})

watch(
  () => route.path,
  (newPath) => {
    if (newPath.endsWith('/bi')) {
      activeTab.value = 'bi'
    } else if (activeTab.value === 'bi') {
      activeTab.value = 'details'
    }
  },
)

const loading = ref(true)
const detailsError = ref(false)
const addressDetails = ref<Record<string, unknown>>({})

// Used when the microtech schema introspection returns no fields, so the
// Stammdaten card and "Felder anpassen" are never empty.
const textField = (accessorKey: string, label: string, graphql: string): FieldInfo => ({
  name: accessorKey.split('_').pop() || accessorKey,
  type: 'String',
  kind: 'SCALAR',
  isRelation: false,
  accessorKey,
  header: label,
  label,
  graphql,
})
const DEFAULT_DETAIL_FIELDS: FieldInfo[] = [
  textField('fldAdrNr', 'Adressnummer', 'fldAdrNr'),
  textField('fldSuchBeg', 'Suchbegriff', 'fldSuchBeg'),
  textField('rowReAnsNr_fldNa2', 'Rechnung: Name 2', 'rowReAnsNr { fldNa2 }'),
  textField('rowReAnsNr_fldStr', 'Rechnung: Straße', 'rowReAnsNr { fldStr }'),
  textField('rowReAnsNr_fldPLZ', 'Rechnung: PLZ', 'rowReAnsNr { fldPLZ }'),
  textField('rowReAnsNr_fldOrt', 'Rechnung: Ort', 'rowReAnsNr { fldOrt }'),
  textField(
    'rowReAnsNr_rowLand_fldLandBez',
    'Rechnung: Land',
    'rowReAnsNr { rowLand { fldLandBez } }',
  ),
  textField('fldStatus', 'Status', 'fldStatus'),
]
const detailFields = computed(() => {
  const selected = selectedColumnsByTab.value.details || []
  const available = availableColumnsByTab.value.details || []

  return selected.map((col) => {
    const found = available.find((a) => (a.accessorKey || a.name) === (col.accessorKey || col.name))
    return found || col
  })
})
const activeTab = ref((route.query.tab as string) || 'details')

watch(
  () => route.query.tab,
  (newTab) => {
    if (newTab && typeof newTab === 'string') {
      activeTab.value = newTab
    } else if (!route.path.endsWith('/bi')) {
      activeTab.value = 'details'
    }
  },
)

watch(
  () => route.path,
  (newPath) => {
    if (newPath.endsWith('/bi')) {
      activeTab.value = 'bi'
    } else if (activeTab.value === 'bi') {
      activeTab.value = (route.query.tab as string) || 'details'
    }
  },
)

const tabs = [
  { label: 'Details', slot: 'details', value: 'details' },
  { label: 'Ansprechpartner', slot: 'ansprechpartner', value: 'ansprechpartner' },
  { label: 'Anschriften', slot: 'anschriften', value: 'anschriften' },
  { label: 'Projekte', slot: 'projects', value: 'projects' },
  { label: 'Projekte Archiv', slot: 'projectsarchive', value: 'projectsarchive' },
  { label: 'Vorgänge', slot: 'transactions', value: 'transactions' },
  { label: 'Vorgänge Archiv', slot: 'transactionsarchive', value: 'transactionsarchive' },
  { label: 'Dokumente', slot: 'dokumente', value: 'dokumente' },
  { label: 'USt-IdNr.-Prüfung', slot: 'vatvalidation', value: 'vatvalidation' },
  { label: 'Auswertungen', slot: 'bi', value: 'bi' },
]

const loadingRelated = ref<Record<string, boolean>>({
  ansprechpartner: false,
  anschriften: false,
  projects: false,
  projectsarchive: false,
  transactions: false,
  transactionsarchive: false,
  dokumente: false,
})

const relatedData = ref<Record<string, Record<string, unknown>[]>>({
  ansprechpartner: [],
  anschriften: [],
  projects: [],
  projectsarchive: [],
  transactions: [],
  transactionsarchive: [],
  dokumente: [],
})

// Pagination state for conRead tabs
const endCursorByTab = ref<Record<string, string>>({
  projects: '',
  projectsarchive: '',
  transactions: '',
  transactionsarchive: '',
  dokumente: '',
})
const hasNextPageByTab = ref<Record<string, boolean>>({
  projects: false,
  projectsarchive: false,
  transactions: false,
  transactionsarchive: false,
  dokumente: false,
})
const limit = ref(1000)

// Column configurations
const availableColumnsByTab = ref<Record<string, FieldInfo[]>>({
  details: [],
  ansprechpartner: [],
  anschriften: [],
  projects: [],
  projectsarchive: [],
  transactions: [],
  transactionsarchive: [],
  dokumente: [],
})

const selectedColumnsByTab = ref<Record<string, FieldInfo[]>>({
  details: [],
  ansprechpartner: [],
  anschriften: [],
  projects: [],
  projectsarchive: [],
  transactions: [],
  transactionsarchive: [],
  dokumente: [],
})

const activeTabKey = computed(() => activeTab.value)

const columnModal = ref()
const modalTitle = computed(
  () => `Spalten anpassen für ${tabs.find((t) => t.value === activeTabKey.value)?.label || ''}`,
)
const modalDescription = ref('Passen Sie die Anzeige der Spalten für die aktuelle Tab-Ansicht an.')

const allPossibleColumns = computed(() => {
  const fields = availableColumnsByTab.value[activeTabKey.value] || []
  return fields.map((f) => ({
    key: f.accessorKey || f.name,
    label: f.label || f.description || f.name,
    locked: isMandatory(f),
  }))
})

const selectedTabColumnsMapped = computed(() => {
  const fields = selectedColumnsByTab.value[activeTabKey.value] || []
  return fields.map((f) => ({
    key: f.accessorKey || f.name || '',
    label: f.label || f.description || f.name || '',
    locked: isMandatory(f),
    graphql: f.graphql,
  }))
})

function selectedTabColumns(tab: string) {
  const fields = selectedColumnsByTab.value[tab] || []
  return fields.map((f) => ({
    key: f.accessorKey || f.name || '',
    label: f.label || f.description || f.name || '',
    locked: isMandatory(f),
    graphql: f.graphql,
  }))
}

function availableColumns(tab: string) {
  const fields = availableColumnsByTab.value[tab] || []
  return fields.map((f) => ({
    key: f.accessorKey || f.name || '',
    label: f.label || f.description || f.name || '',
    graphql: f.graphql,
  }))
}

const onSaveColumns = (newCols: { key: string; label: string }[]) => {
  const key = activeTabKey.value
  const available = availableColumnsByTab.value[key] || []

  // Map back to FieldInfo
  selectedColumnsByTab.value[key] = newCols
    .map((nc) => {
      return available.find((a) => (a.accessorKey || a.name) === nc.key)
    })
    .filter(Boolean) as FieldInfo[]

  saveConfig(key)
}

// Fix for UTabs if it uses 'key' instead of array index

const fetchingSchema = ref(false)
const schemaFetchedByTab = ref<Record<string, boolean>>({
  details: false,
  ansprechpartner: false,
  anschriften: false,
  projects: false,
  projectsarchive: false,
  transactions: false,
  transactionsarchive: false,
  dokumente: false,
})
const schemaErrorByTab = ref<Record<string, boolean>>({})

const openColumnModal = async (tabKey?: string) => {
  const key = tabKey || activeTabKey.value
  columnModal.value?.open()
  const tabLabel = tabs.find((t) => t.value === key)?.label || ''
  console.log(
    'openColumnModal for tab:',
    key,
    'label:',
    tabLabel,
    'schemaFetched:',
    schemaFetchedByTab.value[key],
  )

  // Reset fetching state before fetch
  fetchingSchema.value = !schemaFetchedByTab.value[key]

  if (!schemaFetchedByTab.value[key]) {
    await fetchSchemaForTab(key)
  }
}

const isMandatory = (col: FieldInfo) => {
  const key = activeTabKey.value
  if (key === 'details') return col.accessorKey === 'fldAdrNr'
  return false
}

const onProjectClick = (row: { original: Record<string, unknown> }) => {
  navigateToDetail(row.original, 'projects')
}

const onProjectArchiveClick = (row: { original: Record<string, unknown> }) => {
  navigateToDetail(row.original, 'projectsarchive')
}

const onTransactionClick = (row: { original: Record<string, unknown> }) => {
  navigateToDetail(row.original, 'transactions')
}

const onTransactionArchiveClick = (row: { original: Record<string, unknown> }) => {
  navigateToDetail(row.original, 'transactionsarchive')
}

const onDocumentClick = (row: { original: Record<string, unknown> }) => {
  downloadDocument(row.original)
}

const router = useRouter()

const navigateToDetail = (row: Record<string, unknown>, tabKey: string) => {
  if (tabKey.startsWith('projects')) {
    const prjNr = row.fldPrjNr
    if (prjNr) {
      router.push({
        path: `/microtech-hub/projects/${prjNr}`,
        query: { isArchive: tabKey === 'projectsarchive' ? 'true' : undefined },
      })
    }
  } else if (tabKey.startsWith('transactions')) {
    const belegNr = row.fldBelegNr
    if (belegNr) {
      router.push({
        path: `/microtech-hub/transactions/${belegNr}`,
        query: { isArchive: tabKey === 'transactionsarchive' ? 'true' : undefined },
      })
    }
  }
}

const { downloadDocument } = useDocumentDownload()

const resetToDefault = () => {
  const key = activeTabKey.value
  localStorage.removeItem(`address_detail_${key}_columns`)
  initializeDefaultColumns(key)
}

const saveConfig = (tabKey: string) => {
  const currentCols = selectedColumnsByTab.value[tabKey]
  if (currentCols && currentCols.length > 0) {
    localStorage.setItem(`address_detail_${tabKey}_columns`, JSON.stringify(currentCols))
  }
}

const loadConfig = (tabKey: string) => {
  const saved = localStorage.getItem(`address_detail_${tabKey}_columns`)
  if (saved) {
    try {
      const parsed = JSON.parse(saved)
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Sanity check: Ensure data for 'ansprechpartner' or 'anschriften' does NOT contain
        // common address-specific fields that wouldn't be in those types.
        if (tabKey === 'ansprechpartner' || tabKey === 'anschriften') {
          const hasAddressFields = parsed.some((c: FieldInfo) =>
            ['fldSuchBeg', 'fldStatus', 'fldMatch'].includes(c.accessorKey || ''),
          )
          if (hasAddressFields) {
            console.warn(`Detected address fields in ${tabKey} config, ignoring stale data.`)
            localStorage.removeItem(`address_detail_${tabKey}_columns`)
            return
          }
        }
        selectedColumnsByTab.value[tabKey] = parsed
      }
    } catch (e) {
      console.error(`Failed to load config for ${tabKey}`, e)
    }
  }
}

const fetchSchemaForTab = async (tabKey: string) => {
  fetchingSchema.value = true
  try {
    let tableName = ''
    let directTypeName = ''
    if (tabKey === 'details') tableName = 'tblAddresses'
    else if (tabKey === 'ansprechpartner') directTypeName = 'ContactPersonRowQueryRead'
    else if (tabKey === 'anschriften') directTypeName = 'PostalAddressRowQueryRead'
    else if (tabKey === 'projects' || tabKey === 'projectsarchive')
      directTypeName = 'ProjectRowQueryRead'
    else if (tabKey === 'transactions' || tabKey === 'transactionsarchive')
      directTypeName = 'TransactionRowQueryRead'
    else if (tabKey === 'dokumente') directTypeName = 'DocumentRowQueryRead'

    if (!tableName && !directTypeName) return

    const fields = directTypeName
      ? await fetchRelationFields(directTypeName, customerNo.value, tenantName.value)
      : await fetchTableFields(tableName, customerNo.value, tenantName.value)

    console.log(
      `fetchSchemaForTab ${tabKey} (${tableName || directTypeName}) fields received:`,
      fields.length,
    )

    // Filter out fields that are obviously not for this entity if it's ansprechpartner
    let filteredFields = fields
    if (tabKey === 'ansprechpartner') {
      // We only want fields from ContactPersonRowQueryRead
      // Address fields like fldSuchBeg, fldStatus should NOT be here.
      filteredFields = fields.filter(
        (f) => !['fldSuchBeg', 'fldStatus', 'fldAdrNr_1', 'fldMatch', 'fldAktiv'].includes(f.name),
      )
    } else if (tabKey === 'anschriften') {
      // PostalAddressRowQueryRead fields
      filteredFields = fields.filter((f) => !['fldSuchBeg', 'fldStatus'].includes(f.name))
    } else if (
      tabKey === 'projects' ||
      tabKey === 'projectsarchive' ||
      tabKey === 'transactions' ||
      tabKey === 'transactionsarchive' ||
      tabKey === 'dokumente'
    ) {
      // These types are pretty clean, but filter out standard technical fields if any
      filteredFields = fields.filter((f) => !f.name.startsWith('_') && !f.name.endsWith('_int'))
    }

    if (filteredFields.length === 0) {
      console.warn(`Keine Schema-Felder für ${tableName || directTypeName} gefunden.`)
      // Leave schemaFetchedByTab unset so opening "Felder anpassen" retries, and keep a
      // saved selection instead of replacing it with defaults derived from an empty schema.
      schemaErrorByTab.value[tabKey] = true
      const selected = selectedColumnsByTab.value[tabKey] || []
      if (tabKey === 'details') {
        availableColumnsByTab.value.details = [
          ...DEFAULT_DETAIL_FIELDS,
          ...selected.filter(
            (s) => !DEFAULT_DETAIL_FIELDS.some((d) => d.accessorKey === s.accessorKey),
          ),
        ]
      }
      if (selected.length === 0) {
        initializeDefaultColumns(tabKey)
      }
      return
    }
    schemaErrorByTab.value[tabKey] = false

    const dynamicColumns: FieldInfo[] = []

    // Basis-Felder (SCALAR oder ACO)
    filteredFields.forEach((f: FieldInfo) => {
      if (!f.isRelation && !f.name.startsWith('_') && !f.name.startsWith('dbg')) {
        dynamicColumns.push({
          ...f,
          accessorKey: f.name,
          header: f.label || f.description || f.name,
          label: f.label || f.description || f.name,
          graphql: f.graphql || f.name,
        })
      }
    })

    // Spezielle Relationen für Address Details auflösen
    if (tabKey === 'details') {
      const relsToFetch = ['rowReAnsNr', 'rowLiAnsNr']
      for (const relName of relsToFetch) {
        const relField = filteredFields.find((f) => f.name === relName)
        if (relField) {
          const relFields = await fetchRelationFields(
            relField.type,
            customerNo.value,
            tenantName.value,
          )
          const prefix = relName === 'rowReAnsNr' ? 'Rechnung' : 'Lieferung'

          for (const rf of relFields) {
            // Expanded aco fields in relations will have a dot in the name (e.g. acoGPreis.totalGrossAmount)
            // and they have isRelation: false.
            if (rf.isRelation) {
              if (rf.name === 'rowLand') {
                const landFields = await fetchRelationFields(
                  rf.type,
                  customerNo.value,
                  tenantName.value,
                )
                const landBezField = landFields.find((lf) => lf.name === 'fldLandBez')
                if (landBezField) {
                  dynamicColumns.push({
                    name: landBezField.name,
                    type: landBezField.type,
                    kind: landBezField.kind,
                    isRelation: false,
                    accessorKey: `${relName}_${rf.name}_${landBezField.name}`,
                    header: `${prefix}: Land`,
                    label: `${prefix}: Land`,
                    graphql: landBezField.graphql
                      ? `${relName} { ${rf.name} { ${landBezField.graphql} } }`
                      : `${relName} { ${rf.name} { ${landBezField.name} } }`,
                  })
                }
              }
              continue
            }

            if (rf.name.startsWith('_')) continue

            dynamicColumns.push({
              name: rf.name,
              type: rf.type,
              kind: rf.kind,
              isRelation: false,
              accessorKey: `${relName}_${rf.name}`,
              header: rf.label ? `${prefix}: ${rf.label}` : `${prefix}: ${rf.name}`,
              label: rf.label ? `${prefix}: ${rf.label}` : `${prefix}: ${rf.name}`,
              graphql: rf.graphql ? `${relName} { ${rf.graphql} }` : `${relName} { ${rf.name} }`,
            })
          }
        }
      }
    } else {
      // Relationen für Tabs auflösen (z.B. rowLand in Anschriften)
      for (const f of filteredFields) {
        if (f.isRelation && f.name.startsWith('row')) {
          try {
            const relFields = await fetchRelationFields(f.type, customerNo.value, tenantName.value)
            relFields.forEach((rf) => {
              if (!rf.isRelation && !rf.name.startsWith('_')) {
                dynamicColumns.push({
                  name: rf.name,
                  type: rf.type,
                  kind: rf.kind,
                  isRelation: false,
                  accessorKey: `${f.name}_${rf.name}`,
                  header: rf.label
                    ? `${f.label || f.description || f.name}: ${rf.label}`
                    : `${f.description || f.label || f.name}: ${rf.description || rf.label || rf.name}`,
                  label: rf.label
                    ? `${f.label || f.description || f.name}: ${rf.label}`
                    : `${f.description || f.label || f.name}: ${rf.description || rf.label || rf.name}`,
                  graphql: rf.graphql ? `${f.name} { ${rf.graphql} }` : `${f.name} { ${rf.name} }`,
                })
              }
            })
          } catch (e) {
            console.warn(`Failed to fetch relation fields for ${f.name} (${f.type})`, e)
          }
        }
      }
    }

    availableColumnsByTab.value[tabKey] = dynamicColumns
    schemaFetchedByTab.value[tabKey] = true

    // Update selected columns labels from metadata
    if (selectedColumnsByTab.value[tabKey].length > 0) {
      selectedColumnsByTab.value[tabKey] = selectedColumnsByTab.value[tabKey].map((sc) => {
        const found = dynamicColumns.find(
          (c) => (c.accessorKey || c.name) === (sc.accessorKey || sc.name),
        )
        if (found) {
          return { ...sc, label: found.label, header: found.label, graphql: found.graphql }
        }
        return sc
      })
    }

    // Validate selected columns against new schema
    const currentSelected = selectedColumnsByTab.value[tabKey] || []
    if (currentSelected.length > 0) {
      selectedColumnsByTab.value[tabKey] = currentSelected.filter((sel: FieldInfo) =>
        dynamicColumns.some((avail) => avail.accessorKey === sel.accessorKey),
      )
      if (selectedColumnsByTab.value[tabKey].length === 0) {
        initializeDefaultColumns(tabKey)
      }
    } else {
      initializeDefaultColumns(tabKey)
    }
  } finally {
    fetchingSchema.value = false
  }
}

const initializeDefaultColumns = (tabKey: string) => {
  const avail = availableColumnsByTab.value[tabKey] || []
  let defaults: string[] = []

  const isDetails = tabKey === 'details'
  const isAnsprechpartner = tabKey === 'ansprechpartner'
  const isAnschriften = tabKey === 'anschriften'
  const isProjects = tabKey === 'projects' || tabKey === 'projectsarchive'
  const isTransactions = tabKey === 'transactions' || tabKey === 'transactionsarchive'
  const isDokumente = tabKey === 'dokumente'

  if (isDetails) {
    // requested defaults: adrnr, suchbeg, rena2, restr, replz, reort and reland as TEXT, plus status
    defaults = [
      'fldAdrNr',
      'fldSuchBeg',
      'rowReAnsNr_fldNa2',
      'rowReAnsNr_fldStr',
      'rowReAnsNr_fldPLZ',
      'rowReAnsNr_fldOrt',
      'rowReAnsNr_rowLand_fldLandBez',
      'fldStatus',
    ]
  } else if (isAnsprechpartner) {
    defaults = ['fldName1', 'fldName2']
  } else if (isAnschriften) {
    defaults = ['fldNa1', 'fldNa2', 'fldStr', 'fldPLZ', 'fldOrt']
  } else if (isProjects) {
    defaults = [
      'fldPrjNr',
      'fldSuchBeg',
      'fldBez',
      'fldArt',
      'fldPrio',
      'fldPrjSts',
      'fldErledigtKz',
    ]
  } else if (isTransactions) {
    defaults = [
      'fldBelegNr',
      'fldArt',
      'fldSuchBeg',
      'fldPrjNr',
      'fldDat',
      'fldPrio',
      'fldVerk',
      'acoGPreis.totalNetAmount',
    ]
  } else if (isDokumente) {
    defaults = ['fldNa', 'fldExt']
  }

  // If we have schema, filter to only those that actually exist
  const found =
    avail.length > 0 ? avail.filter((c: FieldInfo) => defaults.includes(c.accessorKey || '')) : []

  // Re-sort found to match defaults order
  if (found.length > 0) {
    found.sort((a, b) => {
      const indexA = defaults.indexOf(a.accessorKey || '')
      const indexB = defaults.indexOf(b.accessorKey || '')
      return indexA - indexB
    })
  }

  // Ensure mandatory for details
  if (isDetails) {
    const mandatoryKey = 'fldAdrNr'
    if (!found.some((c: FieldInfo) => c.accessorKey === mandatoryKey)) {
      const mand = avail.find((c: FieldInfo) => c.accessorKey === mandatoryKey)
      if (mand) found.unshift(mand)
    }
  }

  selectedColumnsByTab.value[tabKey] = found.length > 0 ? found : avail.slice(0, 5)
}

const buildGraphQLQuery = (tabKey: string) => {
  const selected = selectedColumnsByTab.value[tabKey] || []
  const customSelections = new Set<string>()
  const tree: Record<string, unknown> = {}

  // Always include primary key for the entity
  if (tabKey === 'details') {
    tree['fldAdrNr'] = {}
  } else if (tabKey === 'ansprechpartner') {
    // Both fldAdrNr and fldLfdNr might be invalid in ContactPersonRowQueryRead schema
  } else if (tabKey === 'anschriften') {
    tree['fldAnsNr'] = {}
  }

  // Ensure fldExt for icons and preview
  if (tabKey === 'dokumente') {
    tree['fldExt'] = {}
    tree['fldID'] = {}
    tree['fldGUID'] = {}
  }

  selected.forEach((col: FieldInfo) => {
    if (!col.graphql) return
    if (col.graphql.includes('{')) {
      customSelections.add(col.graphql)
    } else {
      const parts = col.graphql.split('.')
      let current = tree as Record<string, unknown>
      parts.forEach((part: string) => {
        if (!current[part]) current[part] = {} as Record<string, unknown>
        current = current[part] as Record<string, unknown>
      })
    }
  })

  const stringifyFields = (obj: Record<string, unknown>): string => {
    const keys = Object.keys(obj)
    if (keys.length === 0) return ''
    return keys
      .map((key) => {
        const subObj = obj[key] as Record<string, unknown>
        const subFields = stringifyFields(subObj)
        return subFields ? `${key} { ${subFields} }` : key
      })
      .join('\n')
  }

  const baseSelection = stringifyFields(tree)
  return [baseSelection, ...Array.from(customSelections)].filter((s) => s).join('\n')
}

const fetchDetails = async () => {
  loading.value = true

  if (!schemaFetchedByTab.value.details) {
    await fetchSchemaForTab('details')
  }

  const selectionSet = buildGraphQLQuery('details')
  const dataQuery = `
    query {
      tblAddresses {
        rowRead(kf1AdrNr: { string: "${addressId.value}" }) {
          fnIsVendor
          ${selectionSet}
        }
      }
    }
  `

  try {
    const response = await axios.post('/api/microtech/v1/proxy/graphql', {
      query: dataQuery,
      variables: {},
    })

    const row = response.data?.data?.tblAddresses?.rowRead
    if (row) {
      console.log(
        'fetchDetails data received:',
        Object.keys(row).length,
        'fields. Keys:',
        Object.keys(row).join(', '),
      )
      addressDetails.value = row
      detailsError.value = false
    } else {
      console.warn('fetchDetails data is empty. Response:', JSON.stringify(response.data))
      detailsError.value = true
    }
  } catch (error) {
    console.error('Failed to fetch address details:', error)
    detailsError.value = true
  } finally {
    loading.value = false
  }
}

const resolveValue = (node: Record<string, unknown>, col: FieldInfo) => {
  if (!col || !col.graphql) return node?.[col?.accessorKey || '']

  // If it's a nested field like "rowReAnsNr { fldOrt }", extract it
  if (col.graphql.includes('{')) {
    const parts = col.graphql.split(/[\s\{\},]+/).filter(Boolean)
    let val: unknown = node
    for (const part of parts) {
      val = (val as Record<string, unknown>)?.[part]
      if (val === undefined || val === null) break
    }
    return val
  }

  // Direct field
  return node?.[col.graphql] || node?.[col.accessorKey || '']
}

const fetchRelatedConRead = async (tabKey: string, loadMore = false) => {
  if (loadMore && !hasNextPageByTab.value[tabKey]) return
  if (loadingRelated.value[tabKey]) return

  if (!schemaFetchedByTab.value[tabKey]) {
    await fetchSchemaForTab(tabKey)
  }

  const selectionSet = buildGraphQLQuery(tabKey)
  let rootField = ''
  let filterField = 'fldAdrNr'
  if (tabKey === 'projects') {
    rootField = 'tblProjects'
    filterField = 'fldAdrNr'
  } else if (tabKey === 'projectsarchive') {
    rootField = 'tblProjectsArchive'
    filterField = 'fldAdrNr'
  } else if (tabKey === 'transactions') {
    rootField = 'tblTransactions'
    filterField = 'fldAdrNr'
  } else if (tabKey === 'transactionsarchive') {
    rootField = 'tblTransactionsArchive'
    filterField = 'fldAdrNr'
  } else if (tabKey === 'dokumente') {
    rootField = 'tblDocuments'
  }

  if (!rootField) return

  loadingRelated.value[tabKey] = true
  try {
    let currentAfter = loadMore && endCursorByTab.value[tabKey] ? endCursorByTab.value[tabKey] : ''
    let moreAvailable = true
    const allFetchedNodes: Record<string, unknown>[] = loadMore
      ? [...relatedData.value[tabKey]]
      : []

    while (moreAvailable) {
      const afterParam = currentAfter ? `, after: "${currentAfter}"` : ''
      const filter =
        tabKey === 'dokumente'
          ? `allBetween: { byNr: { kf1Nr: { string: "${addressId.value}" } } }
             fastFilter: { eq: [{ field: fldDBBkzBez }, { value: "Adr" }] }`
          : `fastFilter: { eq: [{ field: ${filterField}}, { value: "${addressId.value}" }] }`

      const query = `
        query {
          ${rootField} {
            conRead(
              first: ${limit.value}
              ${afterParam}
              ${filter}
            ) {
              pageInfo {
                endCursor
                hasNextPage
              }
              edges {
                node {
                  fldID
                  ${tabKey === 'dokumente' ? 'fldGUID' : ''}
                  ${selectionSet}
                }
              }
            }
          }
        }
      `
      const response = await axios.post('/api/microtech/v1/proxy/graphql', { query, variables: {} })

      const conRead = response.data?.data?.[rootField]?.conRead
      if (!conRead) {
        moreAvailable = false
        break
      }

      const newBatch = conRead.edges.map((edge: { node: Record<string, unknown> }) => edge.node)
      allFetchedNodes.push(...newBatch)

      currentAfter = conRead.pageInfo.endCursor
      moreAvailable = conRead.pageInfo.hasNextPage

      relatedData.value[tabKey] = [...allFetchedNodes]
      endCursorByTab.value[tabKey] = currentAfter
      hasNextPageByTab.value[tabKey] = moreAvailable

      if (newBatch.length === 0) moreAvailable = false
    }
  } catch (error) {
    console.error(`Failed to fetch ${tabKey}:`, error)
  } finally {
    loadingRelated.value[tabKey] = false
  }
}

const fetchRelated = async (tab: string) => {
  const tabKey = tab === 'Ansprechpartner' ? 'ansprechpartner' : 'anschriften'

  if (!schemaFetchedByTab.value[tabKey]) {
    await fetchSchemaForTab(tabKey)
  }

  const selectionSet = buildGraphQLQuery(tabKey)
  const linkName = tabKey === 'ansprechpartner' ? 'lnkContactPeople' : 'lnkPostalAddresses'

  // Prüfe ob rowsRead eine Connection ist (tblAddresses nutzt AddressConnectionQuery)
  const rowType = tabKey === 'ansprechpartner' ? 'ContactPerson' : 'PostalAddress'
  const tableType = `${rowType}TableQueryRead`

  // Die microtech-Links lnkContactPeople und lnkPostalAddresses haben in der Regel
  // direkt rowsRead ohne Connection-Muster (edges/node), es sei denn es ist tblAddresses.
  // Wir prüfen trotzdem sicherheitshalber, aber fangen Fehler ab.
  let isConnection = false
  if (tabKey !== 'ansprechpartner' && tabKey !== 'anschriften') {
    isConnection = await isConnectionType(tableType, customerNo.value, tenantName.value)
  }

  const rowsQuery = isConnection
    ? `rowsRead(byAdrNrAnsNr: { usingAdrNr: {} }) { edges { node { ${selectionSet} } } }`
    : `rowsRead(${tabKey === 'ansprechpartner' ? 'byAdrNrAnsNrNa' : 'byAdrNrAnsNr'}: { usingAdrNr: {} }) { ${selectionSet} }`

  const query = `
    query {
      tblAddresses {
        rowRead(kf1AdrNr: { string: "${addressId.value}" }) {
          ${linkName} {
            ${rowsQuery}
          }
        }
      }
    }
  `

  loadingRelated.value[tabKey] = true
  try {
    const response = await axios.post('/api/microtech/v1/proxy/graphql', { query, variables: {} })

    const node = response.data?.data?.tblAddresses?.rowRead
    if (node) {
      let results = node[linkName]?.rowsRead || []
      if (isConnection && results.edges) {
        results = results.edges.map((e: { node: Record<string, unknown> }) => e.node)
      }

      console.log(`fetchRelated ${tabKey} (${linkName}) rows received:`, results.length)
      if (results.length > 0) {
        console.log(`First row keys for ${tabKey}:`, Object.keys(results[0]).join(', '))
      }
      const currentSelected = selectedColumnsByTab.value[tabKey] || []
      relatedData.value[tabKey] = results.map((r: Record<string, unknown>) => {
        const item: Record<string, unknown> = {}
        currentSelected.forEach((col: FieldInfo) => {
          if (col.accessorKey === 'actions') return
          item[col.accessorKey || ''] = resolveValue(r, col)
        })
        return item
      })
    }
  } catch (error) {
    console.error(`Failed to fetch ${tab}:`, error)
  } finally {
    loadingRelated.value[tabKey] = false
  }
}

watch(activeTab, (val) => {
  const currentTabInQuery = route.query.tab
  const expectedTabInQuery = val === 'details' || val === 'bi' ? undefined : val

  if (val === 'bi') {
    if (!route.path.endsWith('/bi')) {
      router.push({
        path: `/microtech-hub/addresses/${addressId.value}/bi`,
        query: { ...route.query, tab: undefined },
      })
    }
  } else {
    const targetPath = `/microtech-hub/addresses/${addressId.value}`
    if (route.path.endsWith('/bi')) {
      router.push({
        path: targetPath,
        query: { ...route.query, tab: expectedTabInQuery },
      })
    } else if (currentTabInQuery !== expectedTabInQuery) {
      router.push({
        query: { ...route.query, tab: expectedTabInQuery },
      })
    }
  }

  const key = activeTabKey.value
  if (key === 'details') {
    if (Object.keys(addressDetails.value).length === 0) fetchDetails()
  } else if (['ansprechpartner', 'anschriften'].includes(key)) {
    if (relatedData.value[key].length === 0) {
      const tab = tabs.find((t) => t.value === val)
      if (tab) fetchRelated(tab.label)
    }
  } else if (['projects', 'projectsarchive', 'transactions', 'transactionsarchive'].includes(key)) {
    if (relatedData.value[key].length === 0) {
      fetchRelatedConRead(key)
    }
  } else if (key === 'dokumente') {
    if (relatedData.value[key].length === 0) {
      fetchRelatedConRead(key)
    }
  }
})

// Re-fetch when columns change
watch(
  selectedColumnsByTab,
  () => {
    // Only re-fetch for the current active tab
    const key = activeTabKey.value
    if (key === 'details') fetchDetails()
    else if (['ansprechpartner', 'anschriften'].includes(key)) {
      const tab = tabs.find((t) => t.value === key)
      if (tab) fetchRelated(tab.label)
    } else if (
      ['projects', 'projectsarchive', 'transactions', 'transactionsarchive', 'dokumente'].includes(
        key,
      )
    ) {
      fetchRelatedConRead(key)
    }
  },
  { deep: true },
)

if (route.path.endsWith('/bi')) {
  activeTab.value = 'bi'
} else {
  activeTab.value = (route.query.tab as string) || 'details'
}

async function initData() {
  loadConfig('details')
  loadConfig('ansprechpartner')
  loadConfig('anschriften')
  loadConfig('projects')
  loadConfig('projectsarchive')
  loadConfig('transactions')
  loadConfig('transactionsarchive')
  loadConfig('dokumente')

  await fetchDetails()
  // Wait a bit to avoid overwhelming the server with introspection calls
  await new Promise((resolve) => setTimeout(resolve, 500))
  fetchRelated('Ansprechpartner')
  await new Promise((resolve) => setTimeout(resolve, 500))
  fetchRelated('Anschriften')
}

onMounted(async () => {
  if (customerNo.value && tenantName.value) {
    await initData()
  }

  watch([customerNo, tenantName], async () => {
    if (customerNo.value && tenantName.value) {
      await initData()
    }
  })
})
</script>
